import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { CardSizeSelector } from 'components/CardSizeSelector/CardSizeSelector';
import type { CardSize } from 'components/CardSizeSelector/CardSizeSelector';
import { SegmentedToggle } from 'components/SegmentedToggle';
import { useGetActivityStatsQuery } from 'store/services/activity/activityApi';
import { useActivityFeed } from 'hooks/useActivityFeed';
import { Activity } from 'types/interfaces/activity';
import type { FilterToken } from 'screens/Main/Search/searchState';
import ComposeTradeModal, { ComposeTradeRecipient } from 'screens/Main/tabs/TradeAlerts/components/ComposeTradeModal/ComposeTradeModal';
import { SidebarTradeCard } from 'screens/Public/UserProfile/context/ProfileTradeSidebarContext';
import { ACTIVITY_TYPE_CONFIG } from './constants';
import LivePulseDashboard from './components/LivePulseDashboard';
import ActivityFilterBar from './components/ActivityFilterBar';
import ActivityFeed, { ActivityGrouping } from './components/ActivityFeed';
import { activityToStagedCard } from './activityTrade';
import { traderBalanceToken, TRADER_BALANCE_TOKEN_ID } from './traderBalanceToken';
import { useIsMobile } from 'hooks/useIsMobile';
import { useCardFilterState } from 'components/FilterPanel/cardFilterUrlState';
import type { TraderBalanceField } from 'components/FilterPanel/FilterPanel';
import CardFilterRail from 'components/FilterPanel/CardFilterRail';
import { useDrawerLayout } from 'components/FilterPanel/useDrawerLayout';
import CardFilterTokenRow from 'components/FilterPanel/CardFilterTokenRow';
import CardFilterMobile from 'components/FilterPanel/CardFilterMobile';
import FilteredEmptyState from 'components/FilterPanel/FilteredEmptyState';
import 'components/ResultsPanel/ResultsPanel.css';
import './ActivityPage.css';

// /home/activity (Asana 1219008751039721): the results-panel cap owns how the
// feed looks (event pills, grouping, card size, Hide filters), the rail owns
// what it contains (every narrowing filter, Trader balance included), Live /
// Pause sits on the pulse strip, and both groupings render inside the one
// Search-style glass plane. Nothing filters from two places.

const ACTIVITY_FILTER_TOOLTIP = {
  heading: 'Filter recent activity',
  body: "Narrows the events shown below by the card involved and by the trader's balance. Event type pills are separate.",
};

interface ComposeState {
  recipient: ComposeTradeRecipient;
  stagedCard: SidebarTradeCard;
}

export type ActivityViewMode = 'images-S' | 'images-M' | 'images-L';

const VIEW_MODES: ActivityViewMode[] = ['images-S', 'images-M', 'images-L'];

/** The stored value keeps its historical images-* shape; the size selector speaks S/M/L. */
const toCardSize = (mode: ActivityViewMode): CardSize => mode.slice(-1) as CardSize;
const fromCardSize = (size: CardSize): ActivityViewMode => `images-${size}` as ActivityViewMode;

const VIEW_MODE_STORAGE_KEY = 'activity-view-mode';

const getInitialViewMode = (): ActivityViewMode => {
  try {
    const saved = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (saved && VIEW_MODES.includes(saved as ActivityViewMode)) {
      return saved as ActivityViewMode;
    }
  } catch {
    // storage unavailable: fall through to the default
  }
  return 'images-M';
};

/** Trader balance: hook state is number | undefined, the panel's inputs are digit strings. */
const numberToField = (n: number | undefined) => (n === undefined ? '' : String(n));
const fieldToNumber = (s: string) => (s === '' ? undefined : Number(s));

const ActivityPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ActivityViewMode>(getInitialViewMode);
  // Grouping axis (by-card flat feed vs. per-trader groups), session-local like Trade Alerts.
  const [grouping, setGrouping] = useState<ActivityGrouping>('by-card');
  const [compose, setCompose] = useState<ComposeState | null>(null);
  // Search's Hide filters semantics: the rail unmounts, not persisted (the
  // rail's own › folds it to the strip and is persisted; that stays separate).
  const [panelHidden, setPanelHidden] = useState(false);

  const handleViewModeChange = (mode: ActivityViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // storage unavailable: the choice lives for the session
    }
  };

  const isMobile = useIsMobile();
  // Card-attribute filters live in the URL (decision 15); the rail, the token
  // row, the sheet and both feed queries all read this one state.
  const cardFilters = useCardFilterState();
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const layout = useDrawerLayout({ cartOpen: false, railMounted: !isMobile && !panelHidden });

  const {
    activities,
    isLoading,
    hasMore,
    loadMore,
    isPaused,
    togglePause,
    pendingCount,
    activeTypes,
    selectType,
    isSocketConnected,
    newItemIds,
    clearNewItem,
    isFetchingMore,
    scrolledPendingCount,
    showBuffered,
    onScroll,
    traderBalanceMin,
    traderBalanceMax,
    setTraderBalanceMin,
    setTraderBalanceMax,
    lookbackCapped,
  } = useActivityFeed({ filters: cardFilters.requestArgs });

  // Single-select: the lone active filter drives the empty-state copy ("No <label>
  // activity yet.") so a category with nothing to show still explains itself.
  const activeType = Array.from(activeTypes)[0];
  const activeTypeLabel = activeType ? ACTIVITY_TYPE_CONFIG[activeType].label : '';
  // The Affordable feed is a refetched snapshot that bypasses the pause buffer,
  // so Pause would show PAUSED while the feed keeps changing: disabled there.
  const pauseDisabled = activeTypes.has('available');

  // Trader balance stays in the hook (never URL state); the rail and the
  // sheet get it as strings through FilterPanel's optional field (decision 8).
  const traderBalance = useMemo<TraderBalanceField>(
    () => ({
      min: numberToField(traderBalanceMin),
      max: numberToField(traderBalanceMax),
      onChange: ({ min, max }) => {
        if (min !== undefined) setTraderBalanceMin(fieldToNumber(min));
        if (max !== undefined) setTraderBalanceMax(fieldToNumber(max));
      },
    }),
    [traderBalanceMin, traderBalanceMax, setTraderBalanceMin, setTraderBalanceMax]
  );
  const clearTraderBalance = useCallback(() => {
    setTraderBalanceMin(undefined);
    setTraderBalanceMax(undefined);
  }, [setTraderBalanceMin, setTraderBalanceMax]);
  // Reset (rail header), Clear all filters (sheet) and Clear all (cap) all clear both
  const { clearAll: clearCardFilters, clearToken: clearCardToken } = cardFilters;
  const clearAllFilters = useCallback(() => {
    clearCardFilters();
    clearTraderBalance();
  }, [clearCardFilters, clearTraderBalance]);

  // Cap row 2: the URL tokens plus the Trader balance token (decision 6)
  const balanceToken = useMemo(() => traderBalanceToken(traderBalanceMin, traderBalanceMax), [traderBalanceMin, traderBalanceMax]);
  const tokens = useMemo(() => (balanceToken ? [...cardFilters.tokens, balanceToken] : cardFilters.tokens), [cardFilters.tokens, balanceToken]);
  const handleClearToken = useCallback(
    (token: FilterToken) => {
      if (token.id === TRADER_BALANCE_TOKEN_ID) clearTraderBalance();
      else clearCardToken(token);
    },
    [clearCardToken, clearTraderBalance]
  );
  const hasAnyFilter = cardFilters.hasActiveFilters || balanceToken !== null;

  // Open the compose-trade flow for a want_added/available item (send the card to the wanter).
  const handleSend = (activity: Activity) => {
    setCompose({
      recipient: { id: activity.user.id, username: activity.user.username },
      stagedCard: activityToStagedCard(activity),
    });
  };

  const {
    data: stats,
    isLoading: statsLoading,
  } = useGetActivityStatsQuery(undefined, {
    pollingInterval: isSocketConnected ? 0 : 60000,
  });

  // Bind scroll handler to .main-page-content scroll container
  const scrollBound = useRef(false);
  useEffect(() => {
    const scrollContainer = document.querySelector('.main-page-content');
    if (!scrollContainer || scrollBound.current) return;
    scrollBound.current = true;

    const handler = (e: Event) => {
      onScroll(e as unknown as React.UIEvent<HTMLElement>);
    };
    scrollContainer.addEventListener('scroll', handler, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', handler);
      scrollBound.current = false;
    };
  }, [onScroll]);

  // When user clicks "new items" pill, scroll to top then flush
  const handleShowNew = () => {
    const scrollContainer = document.querySelector('.main-page-content');
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
    showBuffered();
  };

  const breadcrumbs = [
    { name: 'Home', url: 'https://trademagic.gg/home' },
    { name: 'Activity', url: 'https://trademagic.gg/home/activity' },
  ];

  return (
    <>
      <SEOHelmet
        title="Activity Feed - TradeMagic"
        description="Watch real-time trading activity across the TradeMagic platform. See what cards are being traded, shipped, and added to want lists."
        canonical="https://trademagic.gg/home/activity"
      />
      <BreadcrumbList breadcrumbs={breadcrumbs} />

      <div className={`activity-page ${layout.pushProps.className}`} style={layout.pushProps.style}>
        <LivePulseDashboard
          stats={stats}
          isLoading={statsLoading}
          isConnected={isSocketConnected}
          isPaused={isPaused}
          togglePause={togglePause}
          pendingCount={pendingCount}
          pauseDisabled={pauseDisabled}
        />

        <div className="results-panel activity-results" data-testid="activity-results-panel">
          {/* The cap: how the feed looks (row 1) and what it is filtered by (row 2). */}
          <div className="results-chrome">
            <div className="results-glass-bar">
              <div className="results-cap-row">
                <ActivityFilterBar activeTypes={activeTypes} selectType={selectType} />
                <div className="results-cap-controls">
                  <SegmentedToggle
                    ariaLabel="Grouping"
                    className="segmented-toggle--panel"
                    value={grouping}
                    onChange={setGrouping}
                    options={[
                      { value: 'by-card', label: 'By card' },
                      { value: 'by-trader', label: 'By trader' },
                    ]}
                  />
                  <CardSizeSelector value={toCardSize(viewMode)} onChange={(size) => handleViewModeChange(fromCardSize(size))} />
                  {!isMobile && (
                    <button type="button" className="toggle-filters-btn" onClick={() => setPanelHidden((h) => !h)} data-testid="activity-toggle-filters">
                      {panelHidden ? '‹ Show filters' : 'Hide filters ›'}
                    </button>
                  )}
                </div>
              </div>

              {/* Row 2: every active filter as a token, including Trader balance (decision 6). */}
              {tokens.length > 0 && (
                <div className="results-cap-row results-cap-row--tokens">
                  <CardFilterTokenRow
                    state={cardFilters.state}
                    tokens={tokens}
                    onChange={cardFilters.update}
                    onClearToken={handleClearToken}
                    onClearAll={clearAllFilters}
                    scrollable={isMobile}
                  />
                </div>
              )}

              {/* The buffered-items pill lives in the cap: the plane below clips
                  overflow, which would pin a sticky pill to the plane's top. */}
              {scrolledPendingCount > 0 && (
                <div className="results-cap-row results-cap-row--new">
                  <button type="button" className="activity-feed__new-items-pill" onClick={handleShowNew}>
                    {scrolledPendingCount} new {scrolledPendingCount === 1 ? 'item' : 'items'}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* The one glass plane. Never a .glass-container or backdrop-filter inside. */}
          <div className="results-glass">
            <div className="results-glass-body">
              {hasAnyFilter && !isLoading && activities.length === 0 ? (
                <FilteredEmptyState
                  listNoun="recent activity"
                  unitNoun="events"
                  unfilteredTotal={0}
                  state={cardFilters.state}
                  tokens={tokens}
                  lastAddedToken={cardFilters.lastAddedToken}
                  onClearToken={handleClearToken}
                  onClearAll={clearAllFilters}
                />
              ) : (
                <ActivityFeed
                  activities={activities}
                  isLoading={isLoading}
                  hasMore={hasMore}
                  loadMore={loadMore}
                  isFetchingMore={isFetchingMore}
                  newItemIds={newItemIds}
                  clearNewItem={clearNewItem}
                  viewMode={viewMode}
                  grouping={grouping}
                  onSend={handleSend}
                  activeTypeLabel={activeTypeLabel}
                />
              )}

              {lookbackCapped && !hasMore && (
                <p className="activity-page__lookback-cap" data-testid="activity-lookback-cap">
                  Showing matching activity from the last 30 days.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* No Trade section here: "Available to send / receive" describe the viewer's
          own lists and have no meaning on a feed of other traders' events (Eric,
          2026-09-29). Same prop the own-list pages and profile tabs use. */}
      {!isMobile && (
        <CardFilterRail
          filters={cardFilters}
          onClearAll={clearAllFilters}
          headerTooltip={ACTIVITY_FILTER_TOOLTIP}
          hidden={panelHidden}
          hideTradeSection
          traderBalance={traderBalance}
        />
      )}
      {isMobile && (
        <CardFilterMobile
          filters={cardFilters}
          onClearAll={clearAllFilters}
          sheetOpen={filterSheetOpen}
          onSheetOpenChange={setFilterSheetOpen}
          hideTradeSection
          traderBalance={traderBalance}
          dockClassName="mobile-search-dock--activity"
        />
      )}

      {compose && (
        <ComposeTradeModal
          isOpen={true}
          onClose={() => setCompose(null)}
          recipient={compose.recipient}
          initialStagedCard={compose.stagedCard}
        />
      )}
    </>
  );
};

export default ActivityPage;
